/**
 * Modal de **alta/edición de rol** (`POST /roles` / `PATCH /roles/<role_id>`).
 *
 * Réplica de `add_role.html`/`edit_role.html` del SSR (`progress/
 * explore_equipo_ssr.md`, §B.7): id del rol (solo en alta, `[a-z0-9_]`), nombre,
 * descripción y permisos por checkboxes (las mismas categorías del catálogo
 * `GET /permissions`). Valida con Zod antes de delegar; el error de la API se
 * traduce a mensaje legible. Permite editar roles del sistema y personalizados
 * (igual que el SSR). Controlado por el padre.
 */
import { useEffect, useMemo, useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
} from "@/components/ui";

import { groupPermissions, teamErrorMessage } from "../format";
import { useCreateRole, useUpdateRole } from "../hooks/useRoleMutations";
import { usePermissions } from "../hooks/useUsers";
import {
  createRoleSchema,
  editRoleSchema,
  type CreateRoleValues,
  type EditRoleValues,
} from "../schemas";
import type { CreateRoleBody, Role, UpdateRoleBody } from "../types";
import { PermissionsCheckboxGroup } from "./PermissionsCheckboxGroup";

interface RoleFormDialogProps {
  /** `null` mantiene el modal cerrado. En `edit`, el rol a editar. */
  target: { mode: "create" } | { mode: "edit"; role: Role } | null;
  onClose: () => void;
}

type FieldErrors = Partial<Record<"role_id" | "name" | "description", string>>;

interface FormState {
  role_id: string;
  name: string;
  description: string;
}

const EMPTY_STATE: FormState = { role_id: "", name: "", description: "" };

export function RoleFormDialog({ target, onClose }: RoleFormDialogProps) {
  const permissionsQuery = usePermissions();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();

  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const isEdit = target?.mode === "edit";

  const categories = useMemo(
    () => groupPermissions(permissionsQuery.data?.items ?? []),
    [permissionsQuery.data],
  );

  useEffect(() => {
    if (target === null) {
      return;
    }
    if (target.mode === "edit") {
      setForm({
        role_id: target.role.id,
        name: target.role.name,
        description: target.role.description ?? "",
      });
      setSelected(new Set(target.role.permissions));
    } else {
      setForm(EMPTY_STATE);
      setSelected(new Set());
    }
    setFieldErrors({});
    createRole.reset();
    updateRole.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  function setField(key: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (target === null) {
      return;
    }

    if (target.mode === "create") {
      const result = createRoleSchema.safeParse(form);
      if (!result.success) {
        setFieldErrors(collectErrors(result.error.flatten().fieldErrors));
        return;
      }
      setFieldErrors({});
      submitCreate(result.data);
    } else {
      const result = editRoleSchema.safeParse(form);
      if (!result.success) {
        setFieldErrors(collectErrors(result.error.flatten().fieldErrors));
        return;
      }
      setFieldErrors({});
      submitEdit(target.role.id, result.data);
    }
  }

  function collectErrors(
    fieldErrorsObj: Partial<Record<keyof FormState, string[] | undefined>>,
  ): FieldErrors {
    const errors: FieldErrors = {};
    (Object.keys(fieldErrorsObj) as (keyof FormState)[]).forEach((key) => {
      const message = fieldErrorsObj[key]?.[0];
      if (message) {
        errors[key] = message;
      }
    });
    return errors;
  }

  function submitCreate(values: CreateRoleValues) {
    const body: CreateRoleBody = {
      role_id: values.role_id,
      name: values.name,
      description: values.description ?? "",
      permissions: Array.from(selected),
    };
    createRole.mutate(body, { onSuccess: onClose });
  }

  function submitEdit(roleId: string, values: EditRoleValues) {
    const body: UpdateRoleBody = {
      name: values.name,
      description: values.description ?? "",
      permissions: Array.from(selected),
    };
    updateRole.mutate({ roleId, body }, { onSuccess: onClose });
  }

  const activeMutation = isEdit ? updateRole : createRole;
  const apiError = activeMutation.isError
    ? teamErrorMessage(activeMutation.error)
    : null;
  const isSubmitting = activeMutation.isPending;

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogTitle>{isEdit ? "Editar rol" : "Nuevo rol"}</DialogTitle>
        <DialogDescription>
          {isEdit
            ? "Modifica el nombre, la descripción y los permisos del rol."
            : "Define un id, un nombre y los permisos del nuevo rol."}
        </DialogDescription>

        <form className="mt-4 flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          {!isEdit && (
            <div className="flex flex-col gap-1">
              <label htmlFor="role-id" className="text-sm font-medium text-slate-700">
                ID del rol
              </label>
              <Input
                id="role-id"
                autoComplete="off"
                placeholder="ej. supervisor_turno"
                value={form.role_id}
                aria-invalid={fieldErrors.role_id ? true : undefined}
                aria-describedby={fieldErrors.role_id ? "role-id-error" : undefined}
                onChange={(event) => setField("role_id", event.target.value)}
              />
              {fieldErrors.role_id && (
                <p id="role-id-error" className="text-sm text-red-600">
                  {fieldErrors.role_id}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label htmlFor="role-name" className="text-sm font-medium text-slate-700">
              Nombre
            </label>
            <Input
              id="role-name"
              autoComplete="off"
              value={form.name}
              aria-invalid={fieldErrors.name ? true : undefined}
              aria-describedby={fieldErrors.name ? "role-name-error" : undefined}
              onChange={(event) => setField("name", event.target.value)}
            />
            {fieldErrors.name && (
              <p id="role-name-error" className="text-sm text-red-600">
                {fieldErrors.name}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="role-description" className="text-sm font-medium text-slate-700">
              Descripción
            </label>
            <textarea
              id="role-description"
              rows={2}
              className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              value={form.description}
              onChange={(event) => setField("description", event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-slate-700">Permisos</span>
            {permissionsQuery.isPending ? (
              <p className="text-sm text-slate-500" role="status">
                Cargando permisos…
              </p>
            ) : permissionsQuery.isError ? (
              <p className="text-sm text-red-600" role="alert">
                {teamErrorMessage(permissionsQuery.error)}
              </p>
            ) : (
              <PermissionsCheckboxGroup
                categories={categories}
                selected={selected}
                onChange={setSelected}
                idPrefix="role-perm"
              />
            )}
          </div>

          {apiError && (
            <p role="alert" className="text-sm text-red-600">
              {apiError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear rol"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
